import React, { useEffect, useState, useCallback } from 'react';
import { FiBookOpen, FiFilter } from 'react-icons/fi';
import { childrenAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ChildrenResults = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [subject, setSubject] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await childrenAPI.getResults({ subject });
      const data = resp?.data?.data || resp?.data || {};
      const list = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
      setItems(list);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError('Please sign in as a parent to view this page.');
      else if (status === 403) setError('Your account is not authorized to view children results.');
      else setError('Failed to load children results');
    } finally {
      setLoading(false);
    }
  }, [subject]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-sm text-slate-500">Loading results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Children results</h1>
            <p className="text-sm text-slate-500 mt-0.5">Recent tests and scores</p>
          </div>
          <div className="flex items-center gap-2">
            <FiFilter className="h-4 w-4 text-slate-400" />
            <select value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-lg border border-slate-200 bg-white shadow-sm px-4 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-slate-200 focus:border-slate-300">
              <option value="">All subjects</option>
              <option value="mathematics">Mathematics</option>
              <option value="english">English</option>
              <option value="physics">Physics</option>
              <option value="chemistry">Chemistry</option>
              <option value="biology">Biology</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 mb-6 text-sm text-red-800">{error}</div>
        )}

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <FiBookOpen className="h-5 w-5 text-slate-500" />
            <span className="text-sm font-medium text-slate-900">Results</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Student</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Test</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Subject</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Score</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((m, i) => (
                  <tr key={i} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-900">{m.studentName || ''}</td>
                    <td className="px-4 py-3 text-slate-600">{m.testTitle || m.title || 'Test'}</td>
                    <td className="px-4 py-3 text-slate-600">{m.subject || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{m.score != null ? `${m.score}%` : '—'}</td>
                    <td className="px-4 py-3 text-slate-500">{m.createdAt ? new Date(m.createdAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildrenResults;
